// shared/document-format.js — the formatting primitives the invoice, quote and
// credit note documents are built from, imported by BOTH runtimes: the PDF
// renderer (server/invoice-pdf.js) and the compose previews (src/js/widgets/*).
//
// Pure: no imports beyond shared/currencies.js, no DOM, no node builtins.
//
// These five functions used to exist twice — once in server/invoice-pdf.js and
// once in src/js/lib/format.js — which is how the compose preview came to format
// dates in the viewer's locale while the PDF used en-GB, and how a date-alignment
// fix landed in the preview (#195) and then again, a day later, in the PDF (#196).
import { isSupportedCurrency } from "./currencies.js";

export function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Bank sort codes render as 12-34-56, grouped as the digits arrive so a
// half-typed code in the compose form still reads correctly.
export function formatSortCode(rawValue) {
  const digits = String(rawValue || "").replace(/\D/g, "").slice(0, 6);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

// Long-form date for the document face: "3 September 2026".
//
// Always en-GB, never the viewer's locale. The document a client receives is
// the PDF, so the compose preview has to render the same string — a US-locale
// browser previewing "September 3, 2026" against a PDF reading "3 September
// 2026" is the preview lying about the document.
export function formatDocumentDate(rawDate) {
  const parsed = rawDate instanceof Date ? rawDate : new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

// Money for the document face, with the symbol and the digits in separate
// spans so the stylesheet can size them independently.
export function formatCurrencyPreviewHtml(rawValue, currency = "GBP") {
  const amount = Number.parseFloat(String(rawValue ?? 0));
  const safe = Number.isFinite(amount) ? amount : 0;
  const code = isSupportedCurrency(currency) ? String(currency).toUpperCase() : "GBP";
  const parts = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).formatToParts(safe);

  return parts
    .map((part) => {
      const value = escapeHtml(part.value);
      if (part.type === "currency") return `<span class="invoice-preview-currency-symbol">${value}</span>`;
      if (part.type === "integer" || part.type === "group" || part.type === "decimal" || part.type === "fraction") {
        return `<span class="invoice-preview-currency-number">${value}</span>`;
      }
      return value;
    })
    .join("");
}

// Escape a value and turn its newlines into <br /> — addresses, notes, and the
// credit note's reason all arrive as multi-line free text.
export function toPreviewMultiline(value, fallback = "") {
  const text = String(value || "").trim();
  if (!text) return escapeHtml(fallback);
  return escapeHtml(text).replace(/\n/g, "<br />");
}

// The time of supply (tax point) line an invoice carries when it differs from
// the issue date.
//
// UK VAT invoices must state a tax point, and it is only the same as the issue
// date when the invoice goes out within 14 days of the work. Beyond that — or
// for work spanning a period — it has to be stated in its own right.
//
// One date or two is the same question asked twice: a job done in a day has a
// supply date, a job done over a fortnight has a supply period. Equal dates
// collapse to the singular rather than printing the same day twice.
export function formatSupplyPeriod(from, to) {
  const start = String(from || "").trim();
  const end = String(to || "").trim();
  if (!start && !end) return "";
  if (!start || start === end) {
    return `Supply date ${formatDocumentDate(end || start)}`;
  }
  if (!end) return `Supply date ${formatDocumentDate(start)}`;
  return `Supply period ${formatDocumentDate(start)} – ${formatDocumentDate(end)}`;
}
